import { FunctionComponent, useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, withStyles } from 'vopay';

import Hubspot, { HubspotData } from './Hubspot/Hubspot';
import Billing, { BillingData } from './Billing/Billing';
import Risk, { RiskData } from './Risk/Risk';
import Review from './Review/Review';
import { Title } from '@/components/UI';
import useR1AccountSubmit from '@/components/Accounts/AccountCreation/R1AccountCreation/useR1AccountSubmit';
import AccountCreatedWithErrorsDialog, {
  AccountCreatedWithErrorsResult,
} from '@/components/Accounts/AccountCreation/R1AccountCreation/AccountCreatedWithErrorsDialog/AccountCreatedWithErrorsDialog';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDialog } from '@/providers/Dialog.provider';

import styles from './R1AccountCreation.module.scss';
import { Prompt } from 'react-router-dom';

const cn = withStyles(styles);

const STEPS = ['details', 'billing', 'risk', 'review'] as const;
type Step = (typeof STEPS)[number];

type FormData = {
  hubspot: HubspotData | null;
  billing: BillingData | null;
  risk: RiskData | null;
};

const R1AccountCreation: FunctionComponent<Record<never, never>> = () => {
  const [step, setStep] = useState<Step>('details');
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation' });
  const alertDialog = useAlertDialog();

  const [formData, setFormData] = useState<FormData>({
    hubspot: null,
    billing: null,
    risk: null,
  });

  const { submit } = useR1AccountSubmit({});
  const dialog = useDialog();
  const history = useHistory();

  const detailsRef = useRef<HTMLDivElement>(null);
  const billingRef = useRef<HTMLDivElement>(null);
  const riskRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const refMap = { details: detailsRef, billing: billingRef, risk: riskRef };
    const activeRef = refMap[step as 'details' | 'billing' | 'risk'];
    const scrollable = activeRef?.current?.firstElementChild as HTMLElement | null;
    if (scrollable) scrollable.scrollTop = 0;
  }, [step]);

  const nextStep = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const prevStep = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    const { hubspot, billing, risk } = formData;
    if (!hubspot || !billing || !risk) return;

    submit({ hubspot, billing, risk }).then(async (result) => {
      if (!result.success) {
        alertDialog.error(
          t('Error.creationFailedTitle', { defaultValue: 'Account Creation Failed' }),
          result.errorMessage ||
            t('Error.creationFailedMessage', { defaultValue: 'An error occurred. Please try again.' }),
        );
        return;
      }

      setSubmitted(true);

      // Account was created, but one or more post-creation sub-requests failed.
      if (result.issues.length > 0) {
        const choice = await dialog.open<AccountCreatedWithErrorsResult>((props) => (
          <AccountCreatedWithErrorsDialog {...props} issues={result.issues} />
        ));
        if (choice === 'goToAccount' && result.accountId) {
          history.push({ pathname: `/accounts/${result.accountId}`, search: result.accountSearch });
        } else {
          // "Fix Later" (or dismiss) → the accounts list
          history.push('/accounts');
        }
        return;
      }

      await alertDialog.success(
        t('Success.createdTitle', { defaultValue: 'Success! Account Created' }),
        t('Success.createdMessage', { defaultValue: 'Your account has been created' }),
        { confirmLabel: t('Success.goToAccounts', { defaultValue: 'Go to Accounts' }) },
      );
      history.push(`/accounts`);
    });
  };

  const displaySteps = [
    t('Steps.details', { defaultValue: 'Details' }),
    t('Steps.billing', { defaultValue: 'Billing' }),
    t('Steps.risk', { defaultValue: 'Risk' }),
  ];
  const currentStepIdx = STEPS.indexOf(step);

  const hasNonEmptyValue = (val: unknown): boolean => {
    if (val === '' || val === null || typeof val === 'undefined') return false;
    if (Array.isArray(val)) return val.some(hasNonEmptyValue);
    if (typeof val === 'object') return Object.values(val).some(hasNonEmptyValue);
    return true;
  };

  const hasPendingData = useMemo(() => {
    const { hubspot, billing, risk } = formData;
    return [hubspot, billing, risk].some((obj) => !!obj && Object.values(obj).some(hasNonEmptyValue));
  }, [formData.hubspot, formData.billing, formData.risk]);

  return (
    <div className={cn('container')}>
      <div className={cn(step === 'review' && 'hidden', 'formContainer')}>
        <Title level="1">{t('title', { defaultValue: 'Add VoPay Account' })}</Title>
        <Card className={cn('card')}>
          <div className={cn(step !== 'details' && 'hidden', 'tabContainer')} ref={detailsRef}>
            <Hubspot
              steps={displaySteps}
              currentStep={currentStepIdx}
              nextStep={nextStep}
              onValuesChange={(v) => setFormData((prev) => ({ ...prev, hubspot: v }))}
            />
          </div>
          <div className={cn(step !== 'billing' && 'hidden', 'tabContainer')} ref={billingRef}>
            <Billing
              steps={displaySteps}
              currentStep={currentStepIdx}
              country={formData.hubspot?.details?.CompanyProfile?.Country ?? ''}
              nextStep={nextStep}
              prevStep={prevStep}
              onValuesChange={(v) => setFormData((prev) => ({ ...prev, billing: v }))}
            />
          </div>
          <div className={cn(step !== 'risk' && 'hidden', 'tabContainer')} ref={riskRef}>
            <Risk
              steps={displaySteps}
              currentStep={currentStepIdx}
              nextStep={nextStep}
              prevStep={prevStep}
              billingData={formData.billing}
              onValuesChange={(v) => setFormData((prev) => ({ ...prev, risk: v }))}
            />
          </div>
        </Card>
      </div>
      <Prompt
        when={hasPendingData && !submitted}
        message={t('Prompt.unsavedChanges', {
          defaultValue: 'Your information will not be saved if you leave now. Are you sure you want to continue?',
        })}
      />
      <div className={cn(step !== 'review' && 'hidden')}>
        <Review
          prevStep={prevStep}
          onSubmit={handleSubmit}
          hubspot={formData.hubspot}
          billing={formData.billing}
          risk={formData.risk}
        />
      </div>
    </div>
  );
};

export default R1AccountCreation;
